import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_SIM_TYPES,
  useManagementCreateSimType,
  useManagementDeleteSimType,
  useManagementGetSimType,
  useManagementUpdateSimType
} from '@apps-common/apis/management/sim-type'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { queryClient } from '@apps-common/lib'
import { ISimTypeModel } from '@apps-common/types/management/sim-type'

export const SimTypeModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { simTypeId: string }
}) => {
  const {
    isLoading,
    isError,
    data: simType
  } = useManagementGetSimType({
    typeId: componentProps?.simTypeId || '',
    config: {
      enabled: !!componentProps?.simTypeId
    }
  })

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<ISimTypeModel>({})

  const onChanged = () => {
    queryClient.refetchQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_SIM_TYPES() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createSimType } = useManagementCreateSimType(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateSimType } = useManagementUpdateSimType(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteSimType } = useManagementDeleteSimType(true, () => onChanged())

  useEffect(() => {
    if (simType) {
      reset(simType)
    }
  }, [simType])

  const onSubmit = async (stData: any) => {
    try {
      if (stData.simTypeId) {
        updateSimType({ model: getValues() })
      } else {
        createSimType({ model: getValues() })
      }
    } catch (e: any) {
      // ignore let mutation handle error
    }
  }

  const isDisabled = (componentProps?.simTypeId && isLoading) || createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-[0px] !h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.simTypeId ? 'Updating' : 'Creating'} title='Sim Type' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.simTypeId && (
              <ModalButton
                component={ConfirmModal}
                classes=''
                disabled={false}
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this sim type. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteSimType({ typeId: componentProps?.simTypeId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[110px] !pb-[70px]'>
        {!isLoading && isError && (
          <p className='mb-[10px] text-error text-sm'>Sim Type failed to load. Please close window and try again.</p>
        )}
        <form noValidate id='simTypeModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputSM
              type='text'
              placeholder='Name of the sim type'
              label='Sim type name'
              id='simTypeName'
              showLabel
              disabled={isDisabled}
              registration={register('simTypeName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.simTypeName}
            />
            <CheckboxInputSM
              label='Is the default sim type'
              id='defaultItem'
              disabled={isDisabled}
              registration={register('defaultItem')}
              errors={errors.defaultItem}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='simTypeModalForm'
            type='submit'
            loading={isDisabled}
            text={simType ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
